import { resolveBlobMimeType } from '@affine/core/utils/blob-file-type';
import type { ExtensionType, Workspace } from '@blocksuite/affine/store';

import {
  type DatabaseImportColumnPayload,
  type DatabaseImportJobStatusChange,
  type DatabaseImportSourcePayload,
  postDatabaseImportBatch,
  waitForDatabaseImportJobs,
} from '../file-import/database-import-client';
import {
  importShardedDatabasesLocalFirst,
  type LocalShardedDatabaseImportProgressChange,
  throwIfLocalDatabaseImportAborted,
} from '../file-import/local-sharded-importer';
import { uploadWorkspaceBlob } from '../workflow-viewer/widgets/upload-blob';
import {
  ATTACHMENT_PLACEHOLDER_MARKER,
  type AttachmentPlaceholder,
  type DatabaseColumn,
  type DatabaseRow,
  isAttachmentPlaceholder,
  isRelationPlaceholder,
  type ParsedDatabase,
  parseNotionHtml,
} from './notion-database-adapter';
import type {
  AttachmentStats,
  DatabaseInfo,
  NotionImportResult,
  NotionZipStructure,
  ZipFileEntry,
} from './types';
import { ZipAnalyzer } from './zip-analyzer';

const DEFAULT_IMPORT_POSITION = { x: 100, y: 100 };
const DATABASE_OFFSET = 420;

const MIME_TYPES: Record<string, string> = {
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  json: 'application/json',
  pdf: 'application/pdf',
  png: 'image/png',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  webp: 'image/webp',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xml: 'application/xml',
  zip: 'application/zip',
};

type ParsedNotionDatabase = {
  clientId: string;
  info: DatabaseInfo;
  parsed: ParsedDatabase;
};

type RowTarget = {
  databaseClientId: string;
  rowId: string;
};

type ImportResolutionStats = {
  relationsResolved: number;
};

export interface NotionDatabaseImportJobOptions {
  workspaceId: string;
  docId: string;
  file: File;
  collection: Workspace;
  docName?: string;
  extensions?: ExtensionType[];
  dropPosition?: { x: number; y: number };
  onJobsChanged?: DatabaseImportJobStatusChange;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  signal?: AbortSignal;
}

function getMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return MIME_TYPES[ext] ?? 'application/octet-stream';
}

function databaseClientId(info: DatabaseInfo, index: number): string {
  return info.notionId ?? info.htmlPath ?? `${info.name}-${index}`;
}

function textFromValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (
    value &&
    typeof value === 'object' &&
    '$blocksuite:internal:text$' in value &&
    Array.isArray((value as { delta?: unknown }).delta)
  ) {
    const textValue = value as unknown as {
      delta: Array<{ insert?: unknown }>;
    };
    return textValue.delta.map(entry => String(entry.insert ?? '')).join('');
  }
  return String(value);
}

function plainData(data: Record<string, unknown> | undefined) {
  if (!data) return undefined;
  return Object.fromEntries(Object.entries(data));
}

function buildUuidTargetMap(
  databases: readonly ParsedNotionDatabase[]
): Map<string, RowTarget> {
  const result = new Map<string, RowTarget>();
  for (const database of databases) {
    for (const [notionUuid, rowId] of Object.entries(
      database.parsed.notionToAffineMapping
    )) {
      result.set(notionUuid, {
        databaseClientId: database.clientId,
        rowId,
      });
    }
  }
  return result;
}

function relationTargetClientId(input: {
  column: DatabaseColumn;
  rowCells: readonly DatabaseRow[];
  uuidTargets: ReadonlyMap<string, RowTarget>;
  databases: readonly ParsedNotionDatabase[];
}): string {
  const targetIds = new Set<string>();
  for (const row of input.rowCells) {
    const value = row[input.column.id]?.value;
    if (!isRelationPlaceholder(value)) continue;
    for (const placeholder of value.placeholders) {
      if (!placeholder.uuid) {
        throw new Error(
          `Relation "${input.column.name}" references "${placeholder.name}" without a Notion row UUID.`
        );
      }
      const target = input.uuidTargets.get(placeholder.uuid);
      if (!target) {
        throw new Error(
          `Relation "${input.column.name}" references missing Notion row UUID "${placeholder.uuid}".`
        );
      }
      targetIds.add(target.databaseClientId);
    }
  }

  if (targetIds.size === 1) return [...targetIds][0]!;
  if (targetIds.size > 1) {
    throw new Error(
      `Relation "${input.column.name}" points to multiple target databases. Split the column before importing.`
    );
  }

  const targetName = input.column.data?.targetDatabaseName;
  if (typeof targetName === 'string' && targetName.trim().length > 0) {
    const target = input.databases.find(
      database => database.info.name === targetName
    );
    if (target) return target.clientId;
  }

  throw new Error(
    `Relation "${input.column.name}" has no resolvable target database.`
  );
}

function importColumns(input: {
  parsed: ParsedDatabase;
  uuidTargets: ReadonlyMap<string, RowTarget>;
  databases: readonly ParsedNotionDatabase[];
}): DatabaseImportColumnPayload[] {
  return input.parsed.columns.map(column => {
    const data = plainData(column.data);
    if (column.type !== 'relation') {
      return {
        id: column.id,
        name: column.name,
        type: column.type,
        data,
      };
    }

    return {
      id: column.id,
      name: column.name,
      type: 'relation',
      data: {
        ...data,
        targetDatabaseClientId: relationTargetClientId({
          column,
          rowCells: input.parsed.rows,
          uuidTargets: input.uuidTargets,
          databases: input.databases,
        }),
        targetFieldId: 'title',
        cardinality: 'many',
      },
    };
  });
}

function findZipFile(
  structure: NotionZipStructure,
  filename: string
): ZipFileEntry | null {
  const decoded = decodeURIComponent(filename);
  return (
    structure.files.find(
      file => file.path === filename || file.name === filename
    ) ??
    structure.files.find(
      file =>
        file.path.endsWith(`/${filename}`) ||
        file.path.endsWith(filename) ||
        file.name === decoded ||
        file.path.endsWith(`/${decoded}`)
    ) ??
    null
  );
}

async function uploadAttachmentPlaceholders(input: {
  workspaceId: string;
  docId: string;
  structure: NotionZipStructure;
  placeholders: AttachmentPlaceholder[];
  stats: AttachmentStats;
}): Promise<
  Record<string, { id: string; name: string; mime: string; order: string }>
> {
  const result: Record<
    string,
    { id: string; name: string; mime: string; order: string }
  > = {};

  for (const [index, placeholder] of input.placeholders.entries()) {
    if (placeholder.isExternal) {
      input.stats.failed += 1;
      input.stats.failedDetails.push({
        filename: placeholder.name,
        reason: 'External URL is not included in the Notion export',
      });
      throw new Error(
        `Attachment "${placeholder.name}" is an external URL and cannot be imported from the Notion ZIP.`
      );
    }

    const file = findZipFile(input.structure, placeholder.relativePath);
    if (!file) {
      input.stats.failed += 1;
      input.stats.failedDetails.push({
        filename: placeholder.name,
        reason: `File not found in ZIP: ${placeholder.relativePath}`,
      });
      throw new Error(
        `Attachment "${placeholder.name}" was not found in the Notion ZIP.`
      );
    }

    const rawBlob = await file.blob();
    const filename =
      placeholder.name ||
      file.name ||
      file.path.split('/').pop() ||
      'attachment';
    const mime = await resolveBlobMimeType(rawBlob, filename);
    const uploadFile = new File([rawBlob], filename, { type: mime });
    const reference = await uploadWorkspaceBlob(input.workspaceId, uploadFile, {
      docId: input.docId,
    });
    result[reference.blobId] = {
      id: reference.blobId,
      name: placeholder.name,
      mime: reference.mimeType || getMimeType(file.path),
      order: `a${index}`,
    };
    input.stats.uploaded += 1;
  }

  return result;
}

async function cellValue(input: {
  column: DatabaseColumn;
  value: unknown;
  workspaceId: string;
  docId: string;
  structure: NotionZipStructure;
  uuidTargets: ReadonlyMap<string, RowTarget>;
  attachmentStats: AttachmentStats;
  resolutionStats: ImportResolutionStats;
}): Promise<unknown> {
  if (isRelationPlaceholder(input.value)) {
    return input.value.placeholders.map(placeholder => {
      if (!placeholder.uuid) {
        throw new Error(
          `Relation "${input.column.name}" references "${placeholder.name}" without a Notion row UUID.`
        );
      }
      const target = input.uuidTargets.get(placeholder.uuid);
      if (!target) {
        throw new Error(
          `Relation "${input.column.name}" references missing Notion row UUID "${placeholder.uuid}".`
        );
      }
      input.resolutionStats.relationsResolved += 1;
      return target.rowId;
    });
  }

  if (isAttachmentPlaceholder(input.value)) {
    return uploadAttachmentPlaceholders({
      workspaceId: input.workspaceId,
      docId: input.docId,
      structure: input.structure,
      placeholders: input.value.placeholders,
      stats: input.attachmentStats,
    });
  }

  if (
    input.value &&
    typeof input.value === 'object' &&
    ATTACHMENT_PLACEHOLDER_MARKER in input.value
  ) {
    throw new Error(`Attachment value in "${input.column.name}" is invalid.`);
  }

  if (input.column.type === 'title' || input.column.type === 'rich-text') {
    return textFromValue(input.value);
  }

  return input.value;
}

async function importRows(input: {
  parsed: ParsedDatabase;
  workspaceId: string;
  docId: string;
  structure: NotionZipStructure;
  uuidTargets: ReadonlyMap<string, RowTarget>;
  attachmentStats: AttachmentStats;
  resolutionStats: ImportResolutionStats;
}) {
  return Promise.all(
    input.parsed.rows.map(async (row, index) => {
      const values: Record<string, unknown> = {};
      for (const column of input.parsed.columns) {
        values[column.id] = await cellValue({
          column,
          value: row[column.id]?.value,
          workspaceId: input.workspaceId,
          docId: input.docId,
          structure: input.structure,
          uuidTargets: input.uuidTargets,
          attachmentStats: input.attachmentStats,
          resolutionStats: input.resolutionStats,
        });
      }
      return {
        id:
          input.parsed.rowIds[index] ??
          `${input.parsed.title}-row-${index + 1}`,
        values,
      };
    })
  );
}

function titleColumnId(parsed: ParsedDatabase): string {
  return (
    parsed.columns.find(column => column.type === 'title')?.id ??
    parsed.columns[0]!.id
  );
}

async function importSource(input: {
  database: ParsedNotionDatabase;
  index: number;
  options: NotionDatabaseImportJobOptions;
  structure: NotionZipStructure;
  uuidTargets: ReadonlyMap<string, RowTarget>;
  databases: readonly ParsedNotionDatabase[];
  attachmentStats: AttachmentStats;
  resolutionStats: ImportResolutionStats;
}): Promise<DatabaseImportSourcePayload> {
  const position = input.options.dropPosition ?? DEFAULT_IMPORT_POSITION;
  return {
    clientId: input.database.clientId,
    title: input.database.parsed.title || input.database.info.name,
    x: position.x + input.index * DATABASE_OFFSET,
    y: position.y,
    columns: importColumns({
      parsed: input.database.parsed,
      uuidTargets: input.uuidTargets,
      databases: input.databases,
    }),
    titleColumnId: titleColumnId(input.database.parsed),
    rows: await importRows({
      parsed: input.database.parsed,
      workspaceId: input.options.workspaceId,
      docId: input.options.docId,
      structure: input.structure,
      uuidTargets: input.uuidTargets,
      attachmentStats: input.attachmentStats,
      resolutionStats: input.resolutionStats,
    }),
    sourceFileName: input.options.file.name,
    sourceSheetName: input.database.info.name,
  };
}

function parseDatabases(structure: NotionZipStructure): ParsedNotionDatabase[] {
  return structure.databases.map((info, index) => {
    const parsed = parseNotionHtml(info.htmlContent)[0];
    if (!parsed) {
      throw new Error(`No database table found in "${info.name}".`);
    }
    if (parsed.columns.length === 0 || parsed.rows.length === 0) {
      throw new Error(`Notion database "${info.name}" is empty.`);
    }
    return {
      clientId: databaseClientId(info, index),
      info,
      parsed: {
        ...parsed,
        title: parsed.title || info.name,
      },
    };
  });
}

export async function importNotionDatabasesWithDatabaseImportJob(
  options: NotionDatabaseImportJobOptions
): Promise<NotionImportResult> {
  const structure = await new ZipAnalyzer().analyze(options.file);
  if (structure.databases.length === 0) {
    throw new Error('No databases found in the Notion export');
  }

  const databases = parseDatabases(structure);
  const uuidTargets = buildUuidTargetMap(databases);
  const attachmentStats: AttachmentStats = {
    uploaded: 0,
    failed: 0,
    failedDetails: [],
  };
  const resolutionStats: ImportResolutionStats = { relationsResolved: 0 };
  const sources = await Promise.all(
    databases.map((database, index) =>
      importSource({
        database,
        index,
        options,
        structure,
        uuidTargets,
        databases,
        attachmentStats,
        resolutionStats,
      })
    )
  );

  const { jobs } = await postDatabaseImportBatch({
    workspaceId: options.workspaceId,
    docId: options.docId,
    sources,
  });
  await waitForDatabaseImportJobs(
    options.workspaceId,
    jobs,
    options.onJobsChanged
  );
  const doc =
    options.collection.getDoc(options.docId) ??
    options.collection.createDoc(options.docId);

  return {
    docId: options.docId,
    doc,
    stats: {
      databaseCount: databases.length,
      rowCount: databases.reduce(
        (sum, database) => sum + database.parsed.rows.length,
        0
      ),
      relationsResolved: resolutionStats.relationsResolved,
      relationsUnresolved: 0,
      attachmentsUploaded: attachmentStats.uploaded,
      attachmentsFailed: attachmentStats.failed,
    },
  };
}

export async function importNotionDatabasesLocalFirst(
  options: NotionDatabaseImportJobOptions
): Promise<NotionImportResult> {
  throwIfLocalDatabaseImportAborted(options.signal);
  options.onProgress?.({
    phase: 'notion-reading',
    processedRows: 0,
    totalRows: 0,
    percent: null,
  });
  const structure = await new ZipAnalyzer().analyze(options.file);
  throwIfLocalDatabaseImportAborted(options.signal);
  if (structure.databases.length === 0) {
    throw new Error('No databases found in the Notion export');
  }

  const databases = parseDatabases(structure);
  throwIfLocalDatabaseImportAborted(options.signal);
  const uuidTargets = buildUuidTargetMap(databases);
  const attachmentStats: AttachmentStats = {
    uploaded: 0,
    failed: 0,
    failedDetails: [],
  };
  const resolutionStats: ImportResolutionStats = { relationsResolved: 0 };
  const sources = await Promise.all(
    databases.map((database, index) =>
      importSource({
        database,
        index,
        options,
        structure,
        uuidTargets,
        databases,
        attachmentStats,
        resolutionStats,
      })
    )
  );
  throwIfLocalDatabaseImportAborted(options.signal);

  const result = await importShardedDatabasesLocalFirst({
    collection: options.collection,
    docId: options.docId,
    sources,
    extensions: options.extensions,
    docName: options.docName,
    onProgress: options.onProgress,
    signal: options.signal,
  });

  return {
    docId: result.docId,
    doc: result.doc,
    stats: {
      databaseCount: databases.length,
      rowCount: databases.reduce(
        (sum, database) => sum + database.parsed.rows.length,
        0
      ),
      relationsResolved: resolutionStats.relationsResolved,
      relationsUnresolved: 0,
      attachmentsUploaded: attachmentStats.uploaded,
      attachmentsFailed: attachmentStats.failed,
    },
  };
}
